import { useCallback, useEffect, type ReactNode } from 'react';
import { ScrollView } from 'react-native';
import { Sheet } from 'tamagui';
import { colors } from '../theme/colors';
import { useOverlayA11y } from '../hooks/useOverlayA11y';
import { pushEscapeHandler } from '../utils/escapeStack';

export interface BaseSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  snapPoints?: number[] | undefined;
  children: ReactNode;
  padding?: string | number | undefined;
  backgroundColor?: string | undefined;
  /** Allow scrolling if content overflows the snap height */
  scrollable?: boolean | undefined;
  /** Optional ARIA label id — paired with `aria-labelledby` so screen
   *  readers announce the sheet's title as its name. */
  ariaLabelledBy?: string | undefined;
}

/**
 * Generic bottom sheet. Phase 5 / ADR-0077 wired the same focus-trap
 * + Esc-dismiss pattern that `AdaptivePanel` got — keyboard parity
 * across both overlay primitives so consumers don't have to remember
 * which surface they're using.
 */
export function BaseSheet({
  open,
  onOpenChange,
  snapPoints = [60],
  children,
  padding = '$4',
  backgroundColor,
  scrollable = true,
  ariaLabelledBy,
}: BaseSheetProps) {
  // Esc dismisses on web — routed through the shared escape stack so
  // only the topmost open overlay handles it. Tamagui's Sheet honours
  // onRequestClose on native already; the stack helper no-ops there.
  const handleClose = useCallback(() => onOpenChange(false), [onOpenChange]);
  useEffect(() => {
    if (!open) return;
    return pushEscapeHandler(handleClose);
  }, [open, handleClose]);

  // Focus trap + dialog ARIA — shared with AdaptivePanel via the
  // useOverlayA11y hook so the contract stays in one place per ADR-0077.
  const overlay = useOverlayA11y({ open, labelId: ariaLabelledBy });

  return (
    <Sheet
      modal
      open={open}
      onOpenChange={onOpenChange}
      snapPoints={snapPoints}
      dismissOnSnapToBottom
      transition="quick"
    >
      <Sheet.Overlay transition="lazy" backgroundColor={colors.overlayScrim} enterStyle={{ opacity: 0 }} exitStyle={{ opacity: 0 }} />
      <Sheet.Handle />
      <Sheet.Frame
        {...({
          padding,
          ref: overlay.ref,
          ...overlay.a11yProps,
          ...(backgroundColor ? { backgroundColor } : {}),
        } as Record<string, unknown>)}
      >
        {scrollable ? (
          <ScrollView showsVerticalScrollIndicator={false} contentContainerStyle={{ flexGrow: 1 }}>
            {children}
          </ScrollView>
        ) : (
          children
        )}
      </Sheet.Frame>
    </Sheet>
  );
}
